/**
 * @module features/entity/linked-entities-display
 * @description Display and render linked entities in the entity detail view
 *
 * This module handles the presentation of related entities (Projects, Opportunities,
 * Leads, Contacts) that are associated with the current entity. It groups entities
 * by type and provides navigation links to related records.
 */

import { validateLinkedEntities } from '../../utils/entity-type-mapper.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { logger } from '../../utils/logger.js';

/**
 * Display linked entities in the provided container
 * Shows related entities grouped by type (Projects, Opportunities, Leads, Contacts)
 *
 * @param {Array} linkedEntities - Array of linked entity objects from API
 * @param {HTMLElement} container - Container element to render the linked entities into
 *
 * @example
 * const metadata = await getEntityMetadata('projects', '12345');
 * const container = document.getElementById('linked-entities-container');
 * displayLinkedEntities(metadata.linkedEntities, container);
 */
export function displayLinkedEntities(linkedEntities, container) {
    if (!container) {
        logger.error('Container element is required for displaying linked entities');
        return;
    }

    // Validate and format linked entities
    const validEntities = validateLinkedEntities(linkedEntities);

    // Hide container if no valid linked entities
    if (validEntities.length === 0) {
        container.classList.add('hidden');
        container.innerHTML = '';
        return;
    }

    // Show container
    container.classList.remove('hidden');

    // Group entities by type
    const grouped = groupLinkedEntitiesByType(validEntities);

    // Render the linked entities section
    container.innerHTML = renderLinkedEntitiesSection(grouped);

    // Add click handlers for navigation
    attachNavigationHandlers(container);

    // Add toggle functionality
    attachToggleHandler(container);
}

/**
 * Group linked entities by their entity type
 *
 * @param {Array} linkedEntities - Array of validated/formatted linked entity objects
 * @returns {Object} Object with arrays grouped by entity type key
 *
 * @example
 * const grouped = groupLinkedEntitiesByType(entities);
 * // Returns: { projects: [...], opportunities: [...], leads: [...], contacts: [...] }
 */
export function groupLinkedEntitiesByType(linkedEntities) {
    const groups = {
        projects: [],
        opportunities: [],
        leads: [],
        contacts: []
    };

    linkedEntities.forEach(entity => {
        const { entityTypeKey } = entity;

        if (groups.hasOwnProperty(entityTypeKey)) {
            groups[entityTypeKey].push(entity);
        } else {
            logger.warn('Unknown entity type key:', entityTypeKey);
        }
    });

    return groups;
}

/**
 * Render the complete linked entities section HTML
 *
 * @param {Object} groupedEntities - Entities grouped by type
 * @returns {string} HTML string for the linked entities section
 * @private
 */
function renderLinkedEntitiesSection(groupedEntities) {
    const groupsHTML = [];

    // Define the order of entity types to display
    const entityTypeOrder = ['projects', 'opportunities', 'leads', 'contacts'];

    entityTypeOrder.forEach(entityType => {
        const entities = groupedEntities[entityType];

        if (entities && entities.length > 0) {
            groupsHTML.push(renderEntityGroup(entityType, entities));
        }
    });

    // If no groups were rendered, return empty string
    if (groupsHTML.length === 0) {
        return '';
    }

    return `
        <div class="linked-entities-section collapsed">
            <h3 class="linked-entities-title">
                <span class="linked-entities-icon">🔗</span>
                Related Entities
                <span class="linked-entities-toggle">▼</span>
            </h3>
            <div class="linked-entities-container">
                ${groupsHTML.join('')}
            </div>
        </div>
    `;
}

/**
 * Render a group of entities of the same type
 *
 * @param {string} entityType - Entity type key (projects, opportunities, leads, contacts)
 * @param {Array} entities - Array of entities of this type
 * @returns {string} HTML string for the entity group
 * @private
 */
function renderEntityGroup(entityType, entities) {
    if (!entities || entities.length === 0) {
        return '';
    }

    // Get config from first entity (all entities in group have same type)
    const { config } = entities[0];

    const entitiesHTML = entities.map(entity => renderLinkedEntityItem(entity)).join('');

    return `
        <div class="linked-entity-group" data-entity-type="${escapeHtml(entityType)}">
            <h4 class="linked-entity-group-header">
                <span class="entity-group-icon">${config.icon}</span>
                <span class="entity-group-label">${escapeHtml(config.labelPlural)}</span>
                <span class="entity-group-count">(${entities.length})</span>
            </h4>
            <div class="linked-entity-list">
                ${entitiesHTML}
            </div>
        </div>
    `;
}

/**
 * Render a single linked entity item
 *
 * @param {Object} entity - Formatted linked entity object
 * @returns {string} HTML string for the entity item
 * @private
 */
function renderLinkedEntityItem(entity) {
    const { entityId, entityName, config, navigationUrl } = entity;

    return `
        <a href="${escapeHtml(navigationUrl)}" 
           class="linked-entity-item" 
           data-entity-id="${escapeHtml(entityId)}"
           data-entity-type="${escapeHtml(entity.entityTypeKey)}"
           title="View ${escapeHtml(config.label)}: ${escapeHtml(entityName)}">
            <span class="linked-entity-icon">${config.icon}</span>
            <span class="linked-entity-info">
                <span class="linked-entity-name">${escapeHtml(entityName)}</span>
                <span class="linked-entity-type">${escapeHtml(config.label)}</span>
            </span>
            <span class="linked-entity-arrow">→</span>
        </a>
    `;
}

/**
 * Attach click handlers for navigation to linked entities
 * Handles the navigation when users click on a linked entity
 *
 * @param {HTMLElement} container - Container element with linked entities
 * @private
 */
function attachNavigationHandlers(container) {
    const entityLinks = container.querySelectorAll('.linked-entity-item');

    entityLinks.forEach(link => {
        link.addEventListener('click', (_event) => {
            // Allow normal link navigation
            // The href is already set correctly, so default behavior will work

            // Optional: Add analytics or tracking here
            const entityType = link.dataset.entityType;
            const entityId = link.dataset.entityId;

            logger.debug('Navigating to linked entity:', { entityType, entityId });
        });
    });
}

/**
 * Attach toggle handler for collapsible section
 * Makes the linked entities section collapsible
 *
 * @param {HTMLElement} container - Container element with linked entities
 * @private
 */
function attachToggleHandler(container) {
    const section = container.querySelector('.linked-entities-section');
    const title = container.querySelector('.linked-entities-title');

    if (!section || !title) return;

    // Always start collapsed on new entity display
    // The section already has 'collapsed' class from renderLinkedEntitiesSection()
    // so we don't need to do anything here for initial state

    title.addEventListener('click', () => {
        section.classList.toggle('collapsed');
    });
}

/**
 * Count total number of linked entities
 *
 * @param {Array} linkedEntities - Array of linked entity objects
 * @returns {number} Total count of valid linked entities
 *
 * @example
 * const count = countLinkedEntities(metadata.linkedEntities); // Returns 5
 */
export function countLinkedEntities(linkedEntities) {
    const validEntities = validateLinkedEntities(linkedEntities);
    return validEntities.length;
}

/**
 * Check if an entity has any linked entities
 *
 * @param {Array} linkedEntities - Array of linked entity objects
 * @returns {boolean} True if there are valid linked entities
 *
 * @example
 * if (hasLinkedEntities(metadata.linkedEntities)) {
 *   displayLinkedEntities(metadata.linkedEntities, container);
 * }
 */
export function hasLinkedEntities(linkedEntities) {
    return countLinkedEntities(linkedEntities) > 0;
}

/**
 * Get count of linked entities by type
 *
 * @param {Array} linkedEntities - Array of linked entity objects
 * @returns {Object} Object with counts by entity type
 *
 * @example
 * const counts = getLinkedEntitiesCounts(metadata.linkedEntities);
 * // Returns: { projects: 2, opportunities: 1, leads: 0, contacts: 3 }
 */
export function getLinkedEntitiesCounts(linkedEntities) {
    const validEntities = validateLinkedEntities(linkedEntities);
    const grouped = groupLinkedEntitiesByType(validEntities);

    return {
        projects: grouped.projects.length,
        opportunities: grouped.opportunities.length,
        leads: grouped.leads.length,
        contacts: grouped.contacts.length
    };
}
